/* Gemeinsames fuer die Diagramme.
 *
 * Alle Diagramme sind von Hand gezeichnetes SVG. Das ist eine bewusste
 * Entscheidung gegen eine Diagrammbibliothek: Der Umfang, den dieses
 * Dashboard braucht - Balken, Linie, Anteile, Rangliste - ist klein, und
 * eine Bibliothek brauchte laufende Pflege, waehrend sie zugleich die
 * Feinheiten verdeckt, auf die es hier ankommt: die Rundung am
 * Balkenende, den Abstand zwischen zwei Flaechen, die Beschriftung nur
 * dort, wo sie traegt.
 */

import { useEffect, useLayoutEffect, useRef, useState } from 'react'

/** Breite des Elternelements, damit das SVG mitwaechst. */
export function useBreite<T extends HTMLElement>(): [React.RefObject<T>, number] {
  const ref = useRef<T>(null)
  const [breite, setBreite] = useState(0)
  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return
    const beobachter = new ResizeObserver(([e]) => setBreite(e.contentRect.width))
    beobachter.observe(el)
    setBreite(el.clientWidth)
    return () => beobachter.disconnect()
  }, [])
  return [ref, breite]
}

/** Breite und verfügbare Höhe eines Diagrammfelds. */
export function useGroesse<T extends HTMLElement>(): [
  React.RefObject<T>, { breite: number; hoehe: number },
] {
  const ref = useRef<T>(null)
  const [groesse, setGroesse] = useState({ breite: 0, hoehe: 0 })
  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return
    const messen = (breite: number, hoehe: number) => setGroesse({ breite, hoehe })
    const beobachter = new ResizeObserver(([e]) =>
      messen(e.contentRect.width, e.contentRect.height))
    beobachter.observe(el)
    messen(el.clientWidth, el.clientHeight)
    return () => beobachter.disconnect()
  }, [])
  return [ref, groesse]
}

export interface HinweisLage { x: number; y: number }

export function useHinweis<T>() {
  const [inhalt, setInhalt] = useState<T | null>(null)
  const [lage, setLage] = useState<HinweisLage>({ x: 0, y: 0 })
  const zeigen = (wert: T, e: { clientX: number; clientY: number }) => {
    setInhalt(wert)
    setLage({ x: e.clientX, y: e.clientY })
  }
  const verbergen = () => setInhalt(null)
  useEffect(() => {
    const weg = () => setInhalt(null)
    window.addEventListener('scroll', weg, true)
    return () => window.removeEventListener('scroll', weg, true)
  }, [])
  return { inhalt, lage, zeigen, verbergen }
}

/**
 * Schoene Achsenschritte.
 *
 * Nicht das Maximum durch fuenf teilen - das ergibt Schritte wie 4.237.
 * Gesucht ist der naechstgroessere Schritt aus 1, 2, 2,5 oder 5 mal einer
 * Zehnerpotenz, denn nur solche Zahlen liest ein Mensch nebenbei.
 */
export function achsenSchritte(max: number, ziel = 4): { schritte: number[]; obergrenze: number } {
  if (!(max > 0)) return { schritte: [0], obergrenze: 1 }
  const roh = max / ziel
  const potenz = Math.pow(10, Math.floor(Math.log10(roh)))
  const rest = roh / potenz
  const stufe = rest <= 1 ? 1 : rest <= 2 ? 2 : rest <= 2.5 ? 2.5 : rest <= 5 ? 5 : 10
  const schritt = stufe * potenz
  const obergrenze = Math.ceil(max / schritt) * schritt
  const schritte: number[] = []
  for (let v = 0; v <= obergrenze + schritt / 2; v += schritt) schritte.push(v)
  return { schritte, obergrenze }
}

/**
 * Achsenbereich, der nicht bei null anfangen muss.
 *
 * Fuer Betraege gilt die Null als Anfang - ein Balken, der nicht auf der
 * Grundlinie steht, taeuscht ueber Verhaeltnisse. Fuer eine Quote gilt
 * das nicht: Eine Stornoquote, die zwischen vier und sechs Prozent
 * schwankt, ist auf einer Achse von null bis sechs eine gerade Linie -
 * und die Frage, ob sich etwas bewegt, bleibt unbeantwortet.
 *
 * Der Bereich wird deshalb um die Daten gelegt und auf schoene Schritte
 * gerundet. Die Achsenbeschriftung zeigt den Anfang, also sieht jeder,
 * dass es nicht die Null ist.
 */
export function achsenBereich(
  min: number, max: number, ziel = 4,
): { schritte: number[]; untergrenze: number; obergrenze: number } {
  if (!isFinite(min) || !isFinite(max)) return { schritte: [0, 1], untergrenze: 0, obergrenze: 1 }
  if (max === min) { min -= 0.5; max += 0.5 }

  const spanne = max - min
  const luft = spanne * 0.15
  const roh = (spanne + 2 * luft) / ziel
  const potenz = Math.pow(10, Math.floor(Math.log10(roh)))
  const rest = roh / potenz
  const stufe = rest <= 1 ? 1 : rest <= 2 ? 2 : rest <= 2.5 ? 2.5 : rest <= 5 ? 5 : 10
  const schritt = stufe * potenz

  const untergrenze = Math.max(0, Math.floor((min - luft) / schritt) * schritt)
  const obergrenze = Math.ceil((max + luft) / schritt) * schritt
  const schritte: number[] = []
  for (let v = untergrenze; v <= obergrenze + schritt / 2; v += schritt) {
    schritte.push(Math.round(v / schritt) * schritt)
  }
  return { schritte, untergrenze, obergrenze }
}

/**
 * Ein Balken mit gerundetem oberen Ende und geradem Fuss.
 *
 * Die Rundung sitzt nur am Datenende. Ein rundum gerundeter Balken loest
 * sich unten von der Grundlinie, und dann laesst sich nicht mehr
 * ablesen, wo die Null liegt.
 */
export function balkenPfad(
  x: number, y: number, breite: number, hoehe: number, r = 4,
): string {
  const rr = Math.max(0, Math.min(r, breite / 2, hoehe))
  if (hoehe <= 0.5) return `M${x} ${y + hoehe} h${breite}`
  return `M${x} ${y + hoehe} V${y + rr} a${rr} ${rr} 0 0 1 ${rr} ${-rr}`
       + ` h${breite - 2 * rr} a${rr} ${rr} 0 0 1 ${rr} ${rr} V${y + hoehe} Z`
}

/** Waagerecht, Rundung rechts. */
export function balkenPfadQuer(
  x: number, y: number, breite: number, hoehe: number, r = 4,
): string {
  const rr = Math.max(0, Math.min(r, hoehe / 2, breite))
  if (breite <= 0.5) return `M${x} ${y} v${hoehe}`
  return `M${x} ${y} h${breite - rr} a${rr} ${rr} 0 0 1 ${rr} ${rr}`
       + ` v${hoehe - 2 * rr} a${rr} ${rr} 0 0 1 ${-rr} ${rr} H${x} Z`
}

/** Die acht Reihenfarben, in fester Reihenfolge. Nie im Kreis weiter. */
export const REIHEN = [
  'var(--reihe-1)', 'var(--reihe-2)', 'var(--reihe-3)', 'var(--reihe-4)',
  'var(--reihe-5)', 'var(--reihe-6)', 'var(--reihe-7)', 'var(--reihe-8)',
] as const

/**
 * Farbe einer Reihe nach ihrem Platz.
 *
 * Ab der neunten gibt es keine neue Farbe, sondern "Sonstige" - eine
 * erzeugte neunte Farbe waere weder unterscheidbar noch geprueft.
 */
export const reihenFarbe = (i: number): string =>
  REIHEN[i] ?? 'var(--schrift-3)'

export const FOLGE = [
  'var(--folge-1)', 'var(--folge-2)', 'var(--folge-3)',
  'var(--folge-4)', 'var(--folge-5)', 'var(--folge-6)',
] as const


/**
 * Luecken in einer Stundenreihe schliessen.
 *
 * Die Auswertung liefert nur Stunden, in denen etwas abgerechnet wurde.
 * Zeichnet man die unveraendert, stehen 14 Uhr und 17 Uhr nebeneinander,
 * und die geschlossene Zeit dazwischen ist unsichtbar - der Tagesverlauf
 * sieht dann durchgehend aus, obwohl der Standort zu hatte.
 */
export function stundenLuecken<T extends { stunde: number }>(
  reihe: T[], leer: (h: number) => T,
): T[] {
  if (!reihe.length) return reihe
  const nach = new Map(reihe.map((r) => [r.stunde, r]))

  // Der Geschäftstag endet nicht um Mitternacht. Ein Standort, der bis
  // ein Uhr offen hat, bucht um 0:04 — nach Uhrzeit sortiert stünde
  // diese Stunde ganz links, vor der Mittagszeit, und der Abend wäre
  // zerschnitten.
  //
  // Wo der Tag beginnt, steht in den Daten selbst: die längste
  // zusammenhängende Lücke ist die Nacht. Dahinter fängt die Reihe an.
  // Das braucht keine Einstellung und gilt für jede Öffnungszeit.
  let luecke = 0, luecken_ab = 0, lauf = 0, lauf_ab = 0
  for (let i = 0; i < 24; i++) {
    const h = i % 24
    if (nach.has(h)) { lauf = 0; continue }
    if (lauf === 0) lauf_ab = h
    lauf++
    if (lauf > luecke) { luecke = lauf; luecken_ab = lauf_ab }
  }
  // Zweiter Durchgang über die Tagesgrenze hinweg: eine Lücke, die um
  // Mitternacht liegt, zählt als eine.
  if (!nach.has(0) && !nach.has(23)) {
    let vorne = 0; while (vorne < 24 && !nach.has(vorne)) vorne++
    let hinten = 0; while (hinten < 24 && !nach.has(23 - hinten)) hinten++
    if (vorne + hinten > luecke) { luecke = vorne + hinten; luecken_ab = 24 - hinten }
  }

  const start = luecke ? (luecken_ab + luecke) % 24
    : Math.min(...reihe.map((r) => r.stunde))
  const raus: T[] = []
  for (let i = 0; i < 24; i++) {
    const h = (start + i) % 24
    // Hinter der letzten belegten Stunde ist Schluss.
    if (raus.length && !nach.has(h)
        && !Array.from({ length: 24 - i }, (_, j) => (h + j) % 24).some((x) => nach.has(x))) break
    raus.push(nach.get(h) ?? leer(h))
  }
  return raus
}
